import React, { useState, useCallback } from 'react';
import { apolloClient, gql } from 'lib/graphql';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import SelectField from 'components/styleguide/select_field/SelectField';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import Button from 'components/styleguide/button/Button';
const styles = require('../onboarding-cards.module.scss');

type Option = { value: string | number; label: string };

interface Education {
  id: number;
  degree: string;
  info: string;
  graduation: number;
  institution: Option;
}

interface EducationCardProps {
  educations: Education[];
  createUrl: string;
  updateUrl: string;
}

const GQL_QUERY_INSTITUTIONS = gql`
  query ($query: String) {
    institutions(query: $query) {
      nodes {
        id
        name
      }
    }
  }
`;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS: Option[] = Array.from({ length: 81 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

const EducationCard: React.FC<EducationCardProps> = ({
  educations: initialEducations,
  createUrl,
  updateUrl,
}) => {
  const [educations, setEducations] = useState<Education[]>(initialEducations || []);
  const [editingId, setEditingId] = useState<number | null>(null);

  const sheet = useEditableSheet({
    url: createUrl,
    method: 'POST',
    payloadKey: 'person_institution',
    onSuccess: (_formData, responseData) => {
      const attrs = responseData.data?.data?.attributes;
      if (!attrs) return;

      const education: Education = {
        id: attrs.id,
        degree: attrs.degree,
        info: attrs.info,
        graduation: attrs.graduation,
        institution: attrs.institution,
      };

      if (editingId != null) {
        setEducations(prev => prev.map(e => e.id === editingId ? education : e));
      } else {
        setEducations(prev => [...prev, education]);
      }
    },
    onDelete: (id: number) => {
      setEducations(prev => prev.filter(e => e.id !== id));
    },
  });

  const hasAny = educations.length > 0;
  const tag = hasAny ? undefined : { variant: 'warning' as const, icon: 'circleAlert' as const, children: 'Missing' };

  const loadInstitutions = useCallback(async (inputValue: string) => {
    if (!inputValue.trim()) return [];

    const result = await apolloClient.query({
      query: GQL_QUERY_INSTITUTIONS,
      variables: { query: inputValue },
    });

    return result.data.institutions.nodes.map((t: { id: string; name: string }) => ({
      label: t.name,
      value: t.id,
    }));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    sheet.open('Add education', {
      degree: '',
      institution_id: '',
      institution: null,
      info: '',
      graduation: '',
    }, { url: createUrl, method: 'POST' });
  };

  const openEdit = (edu: Education) => {
    setEditingId(edu.id);
    sheet.open('Edit education', {
      degree: edu.degree,
      institution_id: edu.institution?.value || '',
      institution: edu.institution || null,
      info: edu.info || '',
      graduation: edu.graduation,
    }, { url: `${updateUrl}/${edu.id}`, method: 'PATCH' });
  };

  const handleDelete = (id: number) => {
    sheet.handleDelete(`${updateUrl}/${id}`, id);
  };

  const selectedInstitution = sheet.formData.institution as Option | null;
  const [institutionInput, setInstitutionInput] = useState('');

  return (
    <>
      <Card
        title="Education"
        iconName="graduationCap"
        context="talent"
        tag={tag}
        onAdd={openAdd}
        allowMultiple={true}
      >
        {educations.map(edu => (
          <CardRow
            key={edu.id}
            context="talent"
            onEdit={() => openEdit(edu)}
            onDelete={() => handleDelete(edu.id)}
          >
            <div className={styles.fieldGridCompact}>
              <p className={styles.inputText}>{edu.degree}</p>
              <p className={styles.label}>
                {edu.institution?.label}
                {edu.graduation && <> &bull; {edu.graduation}</>}
              </p>
            </div>
          </CardRow>
        ))}
      </Card>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Name of Degree"
              placeholder="e.g. Bachelor's of..."
              name="degree"
              required={true}
              value={sheet.formData.degree || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="L"
              error={sheet.getError('degree')}
            />
            <SelectField
              label="Institution"
              name="institution_id"
              placeholder="e.g. University of Lisbon"
              type="async"
              noOptionsMessage={() => 'Type to search'}
              loadOptions={loadInstitutions}
              value={selectedInstitution}
              inputValue={institutionInput}
              onInputChange={(val, { action }) => {
                if (action === 'input-change') setInstitutionInput(val);
                if (action === 'set-value') setInstitutionInput('');
              }}
              onFocus={() => { if (selectedInstitution) setInstitutionInput(selectedInstitution.label); }}
              onChange={(v) => { sheet.handleFormChange('institution', v || null); sheet.handleFormChange('institution_id', v?.value || ''); setInstitutionInput(''); }}
              size="L"
              required={true}
              error={sheet.getError('institution_id')}
            />
            <InputField
              label="Level of Degree"
              placeholder="e.g. Major in..."
              name="info"
              value={sheet.formData.info || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="L"
              error={sheet.getError('info')}
            />
            <SelectField
              label="Graduation Year"
              name="graduation"
              placeholder="e.g. 2019"
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find(o => o.value === sheet.formData.graduation) || null}
              onChange={(v) => sheet.handleFormChange('graduation', v?.value)}
              size="L"
              error={sheet.getError('graduation')}
            />
            {editingId && (
              <Button
                type="secondary"
                size="M"
                iconLeft="trash"
                context="talent"
                onClick={() => handleDelete(editingId)}
              >
                Delete experience
              </Button>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
};

export default EducationCard;
